import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { Heading, LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { useYowanAuthGuard } from '../app/yowanAuthGuards';
import {
  cancelYowanCurrentMemberSeminarApplication,
  fetchYowanCurrentMemberSeminarApplications,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const filterOptions = [
  { value: 'upcoming', label: '参加予定' },
  { value: 'past', label: '参加済み' },
  { value: 'canceled', label: 'キャンセル済み' },
];

const applicationFilterParams = {
  upcoming: { status: 'applied', period: 'upcoming' },
  past: { status: 'applied', period: 'past' },
  canceled: { status: 'canceled', period: 'all' },
};

const getApplicationStatusTone = (row = {}) => {
  if (row.applicationStatus === 'canceled') {
    return 'canceled';
  }

  if (row.isPast) {
    return 'ended';
  }

  return 'active';
};

const getCardTitle = (row = {}) =>
  row.eventSeries?.title || row.eventTitle || 'セミナー';

const getCardSubtitle = (row = {}) =>
  row.eventSeries?.subtitle || row.eventSubtitle || '';

const MyPageSeminarApplicationsPage = ({ guardOptions }) => {
  const content = yowanPageContent['/mypage/seminars'];
  const metadata = getYowanRouteMeta('/mypage/seminars');
  const location = useLocation();
  const guard = useYowanAuthGuard({
    requiresAuth: true,
    search: location.search,
    ...guardOptions,
  });
  const [activeFilter, setActiveFilter] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [operationMessage, setOperationMessage] = useState('');
  const [cancelingId, setCancelingId] = useState('');

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      document
        .querySelector('meta[name="description"]')
        ?.setAttribute('content', metadata.description);
    }
  }, [metadata]);

  useEffect(() => {
    if (guard.status !== 'ready') {
      return;
    }

    let isMounted = true;

    setIsLoading(true);
    setLoadError('');
    setOperationMessage('');

    fetchYowanCurrentMemberSeminarApplications(
      applicationFilterParams[activeFilter] || applicationFilterParams.upcoming,
    )
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setRows(payload.results || []);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setLoadError(content.loadErrorMessage);
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeFilter, content.loadErrorMessage, guard.status]);

  const visibleRows = useMemo(() => rows, [rows]);

  const handleCancel = (row) => {
    const confirmed = window.confirm(content.cancelConfirmMessage);

    if (!confirmed) {
      return;
    }

    setCancelingId(row.id);
    setOperationMessage('');

    cancelYowanCurrentMemberSeminarApplication(row.id)
      .then((updatedRow) => {
        setRows((currentRows) =>
          currentRows
            .map((currentRow) => (currentRow.id === row.id ? updatedRow : currentRow))
            .filter((currentRow) => activeFilter === 'canceled' || currentRow.applicationStatus !== 'canceled'),
        );
        setOperationMessage(content.cancelSuccessMessage);
      })
      .catch(() => {
        setOperationMessage(content.cancelErrorMessage);
      })
      .finally(() => {
        setCancelingId('');
      });
  };

  if (!content) {
    return null;
  }

  return (
    <Section>
      <PageContainer>
        <div className="yowan-mypage-seminars">
          <header className="yowan-mypage-update-header">
            <Heading as="h1" align="left">
              {content.header.title}
            </Heading>
            {content.header.paragraphs.map((paragraph) => (
              <p key={paragraph} className="yowan-mypage-update-header__lead">
                {paragraph}
              </p>
            ))}
            {guard.notice ? (
              <p className="yowan-mypage-update-header__lead">
                {guard.notice.message}
                <br />
                <Link to={guard.notice.actionHref}>{guard.notice.actionLabel}</Link>
              </p>
            ) : null}
          </header>

          {guard.status === 'ready' ? (
            <>
              <div className="yowan-mypage-seminars__toolbar">
                <Link
                  className="yowan-mypage-seminars__back-link"
                  to={appendYowanPreviewQueryIfNeeded('/mypage', location.search)}
                >
                  <span aria-hidden="true">←</span>
                  {content.backToMyPageLabel}
                </Link>
              </div>
              <div className="yowan-mypage-seminars__filters" aria-label={content.filterLabel}>
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`yowan-mypage-seminars__filter${
                      activeFilter === option.value ? ' is-active' : ''
                    }`}
                    type="button"
                    onClick={() => setActiveFilter(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              {isLoading ? (
                <div className="yowan-mypage-seminars__state c-surface-content">
                  <LoadingSpinner label={content.loadingMessage} showLabel />
                </div>
              ) : null}

              {!isLoading && loadError ? (
                <p className="yowan-mypage-update-form__error">{loadError}</p>
              ) : null}

              {operationMessage ? (
                <p className="yowan-mypage-update-form__message">{operationMessage}</p>
              ) : null}

              {!isLoading && !loadError ? (
                <div className="yowan-mypage-seminars__list">
                  {visibleRows.map((row) => {
                    const subtitle = getCardSubtitle(row);
                    const statusTone = getApplicationStatusTone(row);

                    return (
                      <article key={row.id} className="yowan-mypage-seminar-card c-surface-content">
                        <div className="yowan-mypage-seminar-card__main">
                          <div className="yowan-mypage-seminar-card__header">
                            <span className={`yowan-mypage-seminar-card__badge is-${statusTone}`}>
                              {row.statusLabel || '-'}
                            </span>
                            <p className="yowan-mypage-seminar-card__applied">
                              {content.appliedAtLabel}: {row.createdAt || '-'}
                            </p>
                          </div>

                          <h2 className="yowan-mypage-seminar-card__title">{getCardTitle(row)}</h2>
                          {subtitle ? (
                            <p className="yowan-mypage-seminar-card__subtitle">{subtitle}</p>
                          ) : null}

                          <dl className="yowan-mypage-seminar-card__details">
                            <div>
                              <dt>{content.participationLabel}</dt>
                              <dd>
                                {row.slotParticipationLabels?.length ? (
                                  <ul className="yowan-mypage-seminar-card__list">
                                    {row.slotParticipationLabels.map((label) => (
                                      <li key={label}>{label}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  row.slotParticipationLabel || '-'
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt>{content.feeLabel}</dt>
                              <dd>{row.slotFeeLabel || '-'}</dd>
                            </div>
                          </dl>
                        </div>

                        <div className="yowan-mypage-seminar-card__actions">
                          {row.publicDetailHref ? (
                            <Link
                              className="yowan-mypage-seminar-card__link"
                              to={appendYowanPreviewQueryIfNeeded(row.publicDetailHref, location.search)}
                            >
                              {content.publicDetailLabel}
                            </Link>
                          ) : null}
                          {row.canCancel ? (
                            <button
                              className="yowan-mypage-seminar-card__cancel"
                              type="button"
                              disabled={cancelingId === row.id}
                              onClick={() => handleCancel(row)}
                            >
                              {cancelingId === row.id ? content.cancelingLabel : content.cancelLabel}
                            </button>
                          ) : (
                            <p className="yowan-mypage-seminar-card__note">
                              {row.cancelUnavailableReasonLabel || content.cancelUnavailableMessage}
                            </p>
                          )}
                        </div>
                      </article>
                    );
                  })}

                  {!visibleRows.length ? (
                    <div className="yowan-mypage-seminars__state c-surface-content">
                      <p>{content.emptyMessages[activeFilter] || content.emptyMessages.default}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : (
            <div className="yowan-mypage-seminars__state c-surface-content">
              <p>{content.authPendingMessage}</p>
            </div>
          )}
        </div>
      </PageContainer>
    </Section>
  );
};

export default MyPageSeminarApplicationsPage;
